import {
  AfterViewInit, Component, computed, ElementRef, inject, input, OnDestroy, signal, Signal,
  viewChild, WritableSignal,
} from '@angular/core';
import { Router } from '@angular/router';
import { ApiClientConstant } from '@cureskin/api-client';
import { RegimenClass } from '@cureskin/api-client/src/server-api';
import { ButtonCtaComponent } from '@components/button-cta/button-cta.component';
import { EventLoggerService } from '@services/event-logger-service';

const DETECTION_VIDEO_BASE_URL = 'https://cdn.cureskin.com/app/video/take_photo_banner';

@Component({
  selector: 'rebranded-complete-your-profile-card',
  templateUrl: './rebranded-complete-your-profile-card.html',
  imports: [ButtonCtaComponent],
})
export class RebrandedCompleteYourProfileCardComponent implements AfterViewInit, OnDestroy {
  private readonly videoRef = viewChild<ElementRef<HTMLVideoElement>>('videoEl');
  private observer: IntersectionObserver | null = null;

  readonly isVideoReady: WritableSignal<boolean> = signal(false);

  readonly regimenClass = input.required<RegimenClass>();
  readonly gender = input<string>('');

  private readonly router = inject(Router);
  private readonly eventLogger = inject(EventLoggerService);

  readonly aiAnalysisIcon: string = 'https://cdn.cureskin.com/app/img/ai_analysis_icon.svg';
    private readonly videoByClass: Record<string, Record<string, string>> = {
    [ApiClientConstant.Regimen.Class.FACE]: {
      [ApiClientConstant.User.Gender.MALE]: `${DETECTION_VIDEO_BASE_URL}/male_front_face.mp4`,
      [ApiClientConstant.User.Gender.FEMALE]: `${DETECTION_VIDEO_BASE_URL}/female_front_face.mp4`,
    },
    [ApiClientConstant.Regimen.Class.HAIR]: {
      [ApiClientConstant.User.Gender.MALE]: `${DETECTION_VIDEO_BASE_URL}/male_hair_top.mp4`,
      [ApiClientConstant.User.Gender.FEMALE]: `${DETECTION_VIDEO_BASE_URL}/female_hair_top.mp4`,
    },
  };

  private readonly photoTypeByClass: Record<string, string> = {
    [ApiClientConstant.Regimen.Class.FACE]: ApiClientConstant.InstantCheckup.Type.FRONT_FACE,
    [ApiClientConstant.Regimen.Class.HAIR]: ApiClientConstant.InstantCheckup.Type.HAIR_TOP,
  };

  private readonly artGender: Signal<string> = computed((): string =>
    (this.gender() === ApiClientConstant.User.Gender.MALE
      ? ApiClientConstant.User.Gender.MALE
      : ApiClientConstant.User.Gender.FEMALE));

  readonly detectionVideo: Signal<string> = computed((): string =>
    this.videoByClass[this.regimenClass()]?.[this.artGender()] ?? '');

  readonly isFace: Signal<boolean> = computed((): boolean =>
    this.regimenClass() === ApiClientConstant.Regimen.Class.FACE);

  ngAfterViewInit(): void {
    const video = this.videoRef()?.nativeElement;
    if (!video) return;
    if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) this.isVideoReady.set(true);
    video.addEventListener('loadeddata', (): void => this.isVideoReady.set(true));
    video.muted = true;
    if (typeof IntersectionObserver === 'undefined') return;
    this.observer = new IntersectionObserver((entries: IntersectionObserverEntry[]): void => {
      entries.forEach((entry: IntersectionObserverEntry): void => {
        if (entry.isIntersecting) {
          video.play()?.catch((): void => undefined);
          return;
        }
        video.pause();
      });
    }, { threshold: 0 });
    this.observer.observe(video);
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    this.observer = null;
  }

  handleCardClick(): void {
    const type = this.photoTypeByClass[this.regimenClass()];
    if (!type) return;
    this.eventLogger.cleverTapEvent('click', JSON.stringify({
      name: 'rebranded_complete_your_profile', type,
    }));
    this.router.navigate(['/user/instantCheckup/preview'], { queryParams: { tag: type } });
  }
}
